import React, { useCallback, useEffect, useState } from "react";
import { apiGetBrokerOrders } from "api/apiBrokerOrders";
import AntTable from "components/UI/AntTable";
import GenericCard from "components/UI/GenericCard/GenericCard";
import dayjs from "dayjs";
import HoverableTooltip from "components/UI/Tooltip/HoverableTooltip";
import { Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTradingRefresh } from "components/Trading/tradingRefresh";
import DashedTooltip from "components/UI/DashedTooltip";
import {
  formatSlippage,
  VS_ADVISED_TIP,
  VS_MID_TIP,
} from "components/FillQuality/FillQualityTable";

// Home: a collapsed card. Trading page (embedded): a tab's content, loaded on
// mount and on the page's refresh; tradeType "stock" / "option" filters it.
const OrdersStatus = ({ embedded = false, tradeType } = {}) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { refreshKey } = useTradingRefresh();

  const getOrders = async () => {
    try {
      setLoading(true);
      const response = await apiGetBrokerOrders();
      setExecutions(response);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (embedded) getOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, refreshKey]);

  const rows = tradeType
    ? executions.filter((order) => (order.tradeType || "stock") === tradeType)
    : executions;

  const sortByNumber = useCallback((a, b) => {
    // handle null / N/A values
    if (a === b) return 0;
    if (a === null || a === "N/A") return -1;
    if (b === null || b === "N/A") return 1;
    return a > b ? 1 : -1;
  }, []);

  const sortByText = useCallback((a, b) => {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }, []);

  const columns = [
    {
      title: "Symbol",
      dataIndex: "symbol",
      sorter: {
        compare: (a, b) => sortByText(a.symbol, b.symbol),
      },
    },
    {
      title: "Action",
      dataIndex: "side",
      render: (text) => {
        return text === "BOT" ? (
          <span className="green">Buy</span>
        ) : (
          <span className="red">Sell</span>
        );
      },
      sorter: {
        compare: (a, b) => sortByText(a.side, b.side),
      },
    },
    {
      title: "Original price",
      dataIndex: "originalPrice",
      sorter: {
        compare: (a, b) => sortByNumber(a.originalPrice, b.originalPrice),
      },
    },
    {
      title: "Limit price",
      dataIndex: "lmtPrice",
      sorter: {
        compare: (a, b) => sortByNumber(a.lmtPrice, b.lmtPrice),
      },
    },
    {
      title: "Avg price",
      dataIndex: "avgPrice",
      sorter: {
        compare: (a, b) => sortByNumber(a.avgPrice, b.avgPrice),
      },
    },
    {
      title: <DashedTooltip text={VS_MID_TIP}>Vs mid</DashedTooltip>,
      dataIndex: "vsMid",
      render: formatSlippage,
      sorter: {
        compare: (a, b) => sortByNumber(a.vsMid ?? null, b.vsMid ?? null),
      },
    },
    {
      title: <DashedTooltip text={VS_ADVISED_TIP}>Vs advised</DashedTooltip>,
      dataIndex: "vsAdvised",
      render: formatSlippage,
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.vsAdvised ?? null, b.vsAdvised ?? null),
      },
    },
    {
      title: "Algo",
      dataIndex: "algo",
      render: (algo, props) =>
        algo ? `${algo}${props.urgency ? ` / ${props.urgency}` : ""}` : "-",
    },
    // {
    //   title: "Diff",
    //   dataIndex: "priceDifference",
    //   render: (text) => {
    //     if (text === 0) {
    //       return <span>N/A</span>;
    //     }
    //     return text < 0 ? (
    //       <span className="green">{text}%</span>
    //     ) : (
    //       <span className="red">{text}%</span>
    //     );
    //   },
    //   sorter: {
    //     compare: (a, b) => sortByNumber(a.priceDifference, b.priceDifference),
    //   },
    // },
    {
      title: "Shares",
      dataIndex: "cumQty",
      render: (text, properties) => (
        <>
          {text}/{properties.shares}
        </>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.cumQty, b.cumQty),
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (text, props) => {
        if (text === "Filled") {
          return <span className="green">Filled</span>;
        } else if (text === "PendingSubmit" && props.cumQty > 0) {
          return <span className="yellow">Partial Filled</span>;
        } else if (text === "PendingSubmit") {
          return <span className="yellow">Pending</span>;
        } else if (text === "Canceled") {
          return (
            <div className="red">
              <span>Canceled</span>
              {props.cancelReason && (
                <HoverableTooltip text={props.cancelReason} />
              )}
            </div>
          );
        }
        return <span className="red">{text}</span>;
      },
      sorter: {
        compare: (a, b) => sortByText(a.status, b.status),
      },
    },
    {
      title: "Create Time",
      dataIndex: "created",
      render: (text) => <>{dayjs(text).format("HH:mm:ss - DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.time, b.time),
      },
    },
    {
      title: "Update Time",
      dataIndex: "time",
      render: (text) => <>{dayjs(text).format("HH:mm:ss - DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.time, b.time),
      },
    },
  ];

  const refreshButton = (
    <div className="flex justify-end mb-4">
      <Button
        type="primary"
        icon={<ReloadOutlined />}
        onClick={getOrders}
        loading={loading}
      >
        Refresh Data
      </Button>
    </div>
  );

  if (embedded) {
    return (
      <>
        {refreshButton}
        <AntTable rowKey="_id" dataSource={rows} columns={columns} />
      </>
    );
  }

  return (
    <GenericCard
      title="Broker Orders Status"
      className="table-card"
      isCardOpenByDefault={false}
      maxHeight={610}
      onCardOpen={getOrders}
      tooltipText="Execution status of orders placed by the automation system on Interactive Brokers"
    >
      {refreshButton}
      <AntTable rowKey="_id" dataSource={rows} columns={columns} />
    </GenericCard>
  );
};

export default OrdersStatus;
